import { supabase } from './supabaseClient'
import { Case, Customer } from './types/types'
import { RealtimeChannel } from '@supabase/supabase-js'
import { MoveBackwardsDialog } from './NotificationSystem';

export class SupabaseService {
  private casesChannel: RealtimeChannel | null = null;
  private customersChannel: RealtimeChannel | null = null;
  
  // 🚀 START REALTIME LISTENERS
  async startRealtimeSync(
    onCaseChange: (payload: any) => void,
    onCustomerChange: (payload: any) => void
  ) {
    console.log('🚀 Starting Supabase Realtime...');
    
    // Subscribe to cases table
    this.casesChannel = supabase
      .channel('cases-changes')
      .on(
  'postgres_changes',
  { event: '*', schema: 'public', table: 'cases' },
  (payload) => {
    console.log('📨 REALTIME EVENT MOTTATT:', {
      type: payload.eventType,
      table: 'cases',
      id: payload.new?.id || payload.old?.id,
      updatedBy: payload.new?.updated_by
    });
    onCaseChange(payload);
  }
)
      .subscribe((status) => {
        console.log('📡 Cases subscription:', status);
      });

    // Subscribe to customers table
    this.customersChannel = supabase
      .channel('customers-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'customers' },
        (payload) => {
          console.log('📨 Customer change:', payload);
          onCustomerChange(payload);
        }
      )
      .subscribe((status) => {
        console.log('📡 Customers subscription:', status);
      });
  }

// ==================== BRUKERROLLER ====================

// Hent brukerrolle og tillatelser
async getUserRole(userName: string): Promise<{
  role: 'admin' | 'manager' | 'user';
  permissions: {
    can_access_maintenance: boolean;
    can_access_reports: boolean;
    can_manage_users: boolean;
  };
} | null> {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role, permissions')
      .eq('user_name', userName)
      .single();

    if (error || !data) {
      // Hvis bruker ikke finnes, returner standard bruker-rolle
      return {
        role: 'user',
        permissions: {
          can_access_maintenance: false,
          can_access_reports: false,
          can_manage_users: false
        }
      };
    }

    return {
      role: data.role,
      permissions: data.permissions
    };
  } catch (error) {
    console.error('Feil ved henting av brukerrolle:', error);
    return null;
  }
}

// Hent alle brukerroller (for admin-panel)
async getAllUserRoles(): Promise<any[]> {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('*')
      .order('user_name', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Feil ved henting av brukerroller:', error);
    return [];
  }
}

// Oppdater brukerrolle
async updateUserRole(
  userName: string, 
  role: 'admin' | 'manager' | 'user',
  permissions?: {
    can_access_maintenance?: boolean;
    can_access_reports?: boolean;
    can_manage_users?: boolean;
  }
): Promise<boolean> {
  try {
    const updateData: any = { role };
    
    if (permissions) {
      updateData.permissions = permissions;
    }

    const { error } = await supabase
      .from('user_roles')
      .upsert({
        user_name: userName,
        ...updateData,
        updated_at: new Date().toISOString()
      });

    if (error) throw error;

    // Logg endringen
    await this.logSystemEvent('USER_ROLE_UPDATED', {
      user_name: userName,
      new_role: role,
      permissions: permissions,
      updated_by: localStorage.getItem('hagia-user-name')
    });

    return true;
  } catch (error) {
    console.error('Feil ved oppdatering av brukerrolle:', error);
    return false;
  }
}

// Sjekk om bruker har tilgang til vedlikehold
async canAccessMaintenance(userName: string): Promise<boolean> {
  const userRole = await this.getUserRole(userName);
  if (!userRole) return false;
  
  // Admin har alltid tilgang
  if (userRole.role === 'admin') return true;
  
  // Sjekk spesifikk tillatelse
  return userRole.permissions.can_access_maintenance || false;
}

// Sjekk om bruker kan administrere andre brukere
async canManageUsers(userName: string): Promise<boolean> {
  const userRole = await this.getUserRole(userName);
  if (!userRole) return false;
  
  // Bare admin kan administrere brukere
  return userRole.role === 'admin' || userRole.permissions.can_manage_users || false;
}

  // 🛑 STOP REALTIME LISTENERS
  stopRealtimeSync() {
    if (this.casesChannel) {
      supabase.removeChannel(this.casesChannel);
      this.casesChannel = null;
    }
    if (this.customersChannel) {
      supabase.removeChannel(this.customersChannel);
      this.customersChannel = null;
    }
    console.log('🛑 Realtime stopped');
  }

  // 📥 CASES - Last alle aktive cases (ikke arkiverte)
  async loadCases(): Promise<Case[]> {
    try {
      const { data, error } = await supabase
        .from('cases')
        .select('*')
        .order('opprettet', { ascending: false })

      if (error) throw error

      return data.map(this.dbCaseToAppCase)
    } catch (error) {
      console.error('Error loading cases:', error)
      return []
    }
  }

  // ==================== ARKIV HÅNDTERING ====================

  // Arkiver case (flytt fra aktiv til arkiv-tabell)
  async archiveCase(case_: Case): Promise<boolean> {
    try {
      // 1. Lagre til arkiv-tabell
      const { error: archiveError } = await supabase
        .from('archived_cases')
        .insert({
          original_case_id: case_.id,
          case_data: this.appCaseToDbCase(case_),
          archived_by: localStorage.getItem('hagia-user-name') || 'System',
          original_created_at: case_.opprettet,
          category_when_archived: case_.kategori,
          customer_name: case_.kunde,
        });

      if (archiveError) throw archiveError;

      // 2. Slett fra aktiv cases tabell
      const { error: deleteError } = await supabase
        .from('cases')
        .delete()
        .eq('id', case_.id);

      if (deleteError) throw deleteError;

      // 3. Logg hendelsen
      await this.logSystemEvent('CASE_ARCHIVED', {
        original_case_id: case_.id,
        customer: case_.kunde,
        archived_by: localStorage.getItem('hagia-user-name')
      });

      console.log(`✅ Case ${case_.id} arkivert og fjernet fra aktive saker`);
      return true;
    } catch (error) {
      console.error('Feil ved arkivering av case:', error);
      return false;
    }
  }

  // Last arkiverte cases
  async loadArchivedCases(): Promise<Case[]> {
    try {
      const { data, error } = await supabase
        .from('archived_cases')
        .select('*')
        .order('archived_at', { ascending: false });

      if (error) throw error;

      return data.map(archivedCase => {
        const case_ = this.dbCaseToAppCase(archivedCase.case_data);
        case_.kategori = 'Arkiv'; // Sett kategori til Arkiv
        return case_;
      });
    } catch (error) {
      console.error('Feil ved lasting av arkiverte cases:', error);
      return [];
    }
  }

  // Gjenopprett case fra arkiv
  async restoreCaseFromArchive(originalCaseId: number): Promise<boolean> {
    try {
      // 1. Hent fra arkiv
      const { data: archivedCase, error: fetchError } = await supabase
        .from('archived_cases')
        .select('*')
        .eq('original_case_id', originalCaseId)
        .single();

      if (fetchError || !archivedCase) throw fetchError;

      // 2. Gjenopprett til aktiv tabell med ny ID
      const { data: restoredCase, error: insertError } = await supabase
        .from('cases')
        .insert({
          ...archivedCase.case_data,
          id: undefined, // La Supabase generere ny ID
          kategori: 'Service', // Sett til Service som standard
          opprettet: new Date().toISOString(),
          kommentar: `${archivedCase.case_data.kommentar || ''} [GJENOPPRETTET FRA ARKIV]`.trim()
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // 3. Logg gjenoppretting
      await this.logSystemEvent('CASE_RESTORED', {
        original_case_id: originalCaseId,
        new_case_id: restoredCase.id,
        restored_by: localStorage.getItem('hagia-user-name')
      });

      console.log(`✅ Case ${originalCaseId} gjenopprettet som ny case ${restoredCase.id}`);
      return true;
    } catch (error) {
      console.error('Feil ved gjenoppretting av case:', error);
      return false;
    }
  }

  // Slett permanent fra arkiv (kun for admin)
  async permanentDeleteArchivedCase(originalCaseId: number): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('archived_cases')
        .delete()
        .eq('original_case_id', originalCaseId);

      if (error) throw error;

      await this.logSystemEvent('CASE_PERMANENTLY_DELETED', {
        original_case_id: originalCaseId,
        deleted_by: localStorage.getItem('hagia-user-name')
      });

      console.log(`⚠️ Case ${originalCaseId} slettet permanent fra arkiv`);
      return true;
    } catch (error) {
      console.error('Feil ved permanent sletting:', error);
      return false;
    }
  }

  // ==================== BACKUP & LOGGING FUNCTIONS ====================

  // Log system events (login, import, export etc)
  async logSystemEvent(
    eventType: string, 
    eventData: any = {}
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('system_events')
        .insert({
          event_type: eventType,
          event_data: eventData,
          user_name: localStorage.getItem('hagia-user-name') || 'Unknown',
          ip_address: await this.getClientIP(),
          user_agent: navigator.userAgent
        });

      if (error) {
        console.error('Failed to log system event:', error);
      } else {
        console.log(`📝 System event logged: ${eventType}`);
      }
    } catch (error) {
      console.error('Error logging system event:', error);
    }
  }

  // Get client IP (for logging)
  private async getClientIP(): Promise<string> {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch {
      return 'Unknown';
    }
  }

  // Manual backup til backup tables (utover automatisk trigger)
  async backupCustomer(
    customer: Customer, 
    action: 'CREATE' | 'UPDATE' | 'DELETE'
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('customers_backup')
        .insert({
          customer_id: customer.id,
          action: action,
          customer_data: customer,
          changed_by: localStorage.getItem('hagia-user-name') || 'Unknown',
          ip_address: await this.getClientIP(),
          user_agent: navigator.userAgent
        });

      if (error) {
        console.error('Failed to backup customer:', error);
      }
    } catch (error) {
      console.error('Error backing up customer:', error);
    }
  }

  // Manual backup av case
  async backupCase(
  caseItem: Case, 
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'CATEGORY_CHANGE',
  previousData?: Case
): Promise<void> {
  try {
    console.log(`💾 Lagrer backup: Case ${caseItem.id} - ${action}`);
    
    const { error } = await supabase
      .from('cases_backup')
      .insert({
        case_id: caseItem.id,
        action: action,
        case_data: caseItem,
        previous_data: previousData || null,
        changed_by: localStorage.getItem('hagia-user-name') || 'Unknown',
        changed_at: new Date().toISOString(),
        ip_address: await this.getClientIP(),
        user_agent: navigator.userAgent
      });

    if (error) {
      console.error('Failed to backup case:', error);
      
      // Sjekk om backup-tabellen eksisterer
      if (error.code === '42P01') {
        console.warn('⚠️ cases_backup tabellen eksisterer ikke');
        console.warn('Kjør SQL-script for å opprette backup-tabeller');
      }
    } else {
      console.log(`✅ Case backup lagret for ${caseItem.id}`);
    }
  } catch (error) {
    console.error('Error backing up case:', error);
  }
}

  // Hent backup historie for en kunde
  async getCustomerBackupHistory(customerId: number): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('customers_backup')
        .select('*')
        .eq('customer_id', customerId)
        .order('changed_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching customer backup history:', error);
      return [];
    }
  }

  // Hent backup historie for en case
  async getCaseBackupHistory(caseId: number): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('cases_backup')
        .select('*')
        .eq('case_id', caseId)
        .order('changed_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching case backup history:', error);
      return [];
    }
  }

  // Lag daily snapshot (kan kalles manuelt eller via scheduled job)
  async createDailySnapshot(): Promise<boolean> {
    try {
      const { error } = await supabase
        .rpc('create_daily_snapshot');

      if (error) throw error;
      
      console.log('✅ Daily snapshot created');
      await this.logSystemEvent('DAILY_SNAPSHOT', {
        timestamp: new Date().toISOString()
      });
      
      return true;
    } catch (error) {
      console.error('Error creating daily snapshot:', error);
      return false;
    }
  }

  // Hent siste endringer (for dashboard)
  async getRecentChanges(limit: number = 50): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('recent_changes')
        .select('*')
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching recent changes:', error);
      return [];
    }
  }

  // Restore fra backup (nødsituasjon)
  async restoreFromBackup(
    backupId: number, 
    type: 'customer' | 'case'
  ): Promise<boolean> {
    try {
      if (type === 'customer') {
        const { data: backup, error } = await supabase
          .from('customers_backup')
          .select('customer_data')
          .eq('id', backupId)
          .single();

        if (error || !backup) throw error;

        const customer = backup.customer_data as any;
        const { error: restoreError } = await supabase
          .from('customers')
          .upsert(customer);

        if (restoreError) throw restoreError;
        
        await this.logSystemEvent('RESTORE_CUSTOMER', {
          backup_id: backupId,
          customer_id: customer.id
        });
        
      } else if (type === 'case') {
        const { data: backup, error } = await supabase
          .from('cases_backup')
          .select('case_data')
          .eq('id', backupId)
          .single();

        if (error || !backup) throw error;

        const caseItem = backup.case_data as any;
        const { error: restoreError } = await supabase
          .from('cases')
          .upsert(caseItem);

        if (restoreError) throw restoreError;
        
        await this.logSystemEvent('RESTORE_CASE', {
          backup_id: backupId,
          case_id: caseItem.id
        });
      }

      console.log(`✅ Restored ${type} from backup #${backupId}`);
      return true;
    } catch (error) {
      console.error(`Error restoring ${type} from backup:`, error);
      return false;
    }
  }

  // Statistikk over backup
  async getBackupStats(): Promise<any> {
    try {
      const [customersCount, casesCount, eventsCount, snapshotsCount] = await Promise.all([
        supabase.from('customers_backup').select('id', { count: 'exact', head: true }),
        supabase.from('cases_backup').select('id', { count: 'exact', head: true }),
        supabase.from('system_events').select('id', { count: 'exact', head: true }),
        supabase.from('daily_snapshots').select('id', { count: 'exact', head: true })
      ]);

      return {
        total_customer_backups: customersCount.count || 0,
        total_case_backups: casesCount.count || 0,
        total_system_events: eventsCount.count || 0,
        total_daily_snapshots: snapshotsCount.count || 0,
        last_backup: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error getting backup stats:', error);
      return null;
    }
  }

  // ==================== STANDARD CASE OPERATIONS ====================

 // Lagre/oppdater case (bruker archiveCase hvis kategori=Arkiv)
async saveCase(case_: Case): Promise<boolean> {
  try {
    // Forbedret duplikatsjekk for nye ordre
    if (!case_.id || typeof case_.id === 'number') {
      const tenSecondsAgo = new Date(Date.now() - 10000).toISOString();
      
      // Hent alle nylige cases
      const { data: recentCases, error: checkError } = await supabase
        .from('cases')
        .select('id, kunde, beskrivelse, maskinMerke, opprettet')
        .gte('opprettet', tenSecondsAgo);

      // Manuell filtrering for å unngå problemer med spesialtegn
      const duplicates = recentCases?.filter(rc => 
        rc.kunde === case_.kunde && 
        rc.beskrivelse === case_.beskrivelse && 
        rc.maskinMerke === case_.maskinMerke
      ) || [];

      if (duplicates.length > 0) {
        console.warn('Duplikat ordre oppdaget:', duplicates);
        return false;
      }
    }
    
    // Hvis kategori er "Arkiv", arkiver i stedet
    if (case_.kategori === 'Arkiv') {
      return await this.archiveCase(case_);
    }

    // Sjekk eksisterende case
    let existingCase = null;
    if (case_.id) {
      const { data } = await supabase
        .from('cases')
        .select('*')
        .eq('id', case_.id)
        .maybeSingle(); // Bruk maybeSingle for å unngå feil hvis ikke funnet
      existingCase = data;
    }

    const dbCase = this.appCaseToDbCase(case_);
    dbCase.last_updated = new Date().toISOString();
    dbCase.updated_by = localStorage.getItem('hagia-user-name') || 'System';
    
    // Valideringer og advarsler (samme som før)
    if (existingCase && existingCase.kategori === 'Arkiv') {
      alert('Arkiverte ordre kan ikke endres!');
      return false;
    }
    
    // Lagre til database
    let error;
    if (existingCase) {
      const result = await supabase
        .from('cases')
        .update(dbCase)
        .eq('id', case_.id);
      error = result.error;
    } else {
      const result = await supabase
        .from('cases')
        .insert([dbCase]);
      error = result.error;
    }

    if (error) {
      console.error('Supabase error:', error);
      
      // Håndter 406 spesielt
      if (error.code === '406') {
        console.warn('406 Error - prøver uten select');
        const retryResult = existingCase
          ? await supabase.from('cases').update(dbCase).eq('id', case_.id)
          : await supabase.from('cases').insert([dbCase]);
        
        if (!retryResult.error) {
          console.log('✅ Lagret uten select');
        } else {
          throw retryResult.error;
        }
      } else {
        throw error;
      }
    }

    // Logg historikk
    if (existingCase && existingCase.kategori !== case_.kategori) {
      await this.saveCaseHistory(
        case_.id,
        `Flyttet fra ${existingCase.kategori} til ${case_.kategori}`,
        case_.kategori,
        dbCase.updated_by
      );
      // Backup ved kategori-endring
      await this.backupCase(case_, 'CATEGORY_CHANGE', existingCase);
    } else if (!existingCase && case_.id) {
      await this.saveCaseHistory(
        case_.id,
        'Sak opprettet',
        case_.kategori,
        dbCase.updated_by
      );
      // Backup ved ny case
      await this.backupCase(case_, 'CREATE');
    } else if (existingCase) {
      // Backup ved oppdatering
      await this.backupCase(case_, 'UPDATE', existingCase);
    }

    console.log(`✅ Case ${case_.id} lagret successfully`);
    return true;
  } catch (error) {
    console.error('❌ Error saving case:', error);
    return false;
  }
}

  // 🗑️ CASE - Flytt til papirkurv
async moveToTrash(case_: Case): Promise<boolean> {
  try {
    console.log(`🗑️ Flytter case ${case_.id} til papirkurv...`);
    
    // 1. Lagre til papirkurv-tabell
    const { error: trashError } = await supabase
      .from('deleted_cases')
      .insert({
        original_case_id: case_.id,
        case_data: this.appCaseToDbCase(case_),
        deleted_by: localStorage.getItem('hagia-user-name') || 'System',
        original_created_at: case_.opprettet,
        category_when_deleted: case_.kategori
      });

    if (trashError) throw trashError;

    // 2. Slett fra aktiv cases tabell
    const { error: deleteError } = await supabase
      .from('cases')
      .delete()
      .eq('id', case_.id);

    if (deleteError) throw deleteError;

    // 3. Logg hendelsen
    await this.logSystemEvent('CASE_MOVED_TO_TRASH', {
      original_case_id: case_.id,
      customer: case_.kunde,
      deleted_by: localStorage.getItem('hagia-user-name')
    });

    console.log(`✅ Case ${case_.id} flyttet til papirkurv`);
    return true;
  } catch (error) {
    console.error('Feil ved flytting til papirkurv:', error);
    return false;
  }
}

// 📋 PAPIRKURV - Last slettede cases
async loadDeletedCases(): Promise<Case[]> {
  try {
    const { data, error } = await supabase
      .from('deleted_cases')
      .select('*')
      .order('deleted_at', { ascending: false });

    if (error) throw error;

    return data.map(deletedCase => {
      const case_ = this.dbCaseToAppCase(deletedCase.case_data);
      case_.kategori = 'Papirkurv';
      return case_;
    });
  } catch (error) {
    console.error('Feil ved lasting av papirkurv:', error);
    return [];
  }
}

// ♻️ PAPIRKURV - Gjenopprett case
async restoreCaseFromTrash(originalCaseId: number): Promise<boolean> {
  try {
    console.log(`♻️ Gjenoppretter case ${originalCaseId} fra papirkurv...`);
    
    // 1. Hent fra papirkurv
    const { data: deletedCase, error: fetchError } = await supabase
      .from('deleted_cases')
      .select('*')
      .eq('original_case_id', originalCaseId)
      .single();

    if (fetchError || !deletedCase) throw fetchError;

    // 2. Gjenopprett til aktiv tabell med SAMME ID
    const caseData = deletedCase.case_data;
    const { error: insertError } = await supabase
      .from('cases')
      .insert({
        ...caseData,
        id: originalCaseId // Behold original ID
      });

    if (insertError) throw insertError;

    // 3. Slett fra papirkurv
    const { error: deleteError } = await supabase
      .from('deleted_cases')
      .delete()
      .eq('original_case_id', originalCaseId);

    if (deleteError) throw deleteError;

    // 4. Logg hendelsen
    await this.logSystemEvent('CASE_RESTORED', {
      case_id: originalCaseId,
      restored_by: localStorage.getItem('hagia-user-name')
    });

    console.log(`✅ Case ${originalCaseId} gjenopprettet`);
    return true;
  } catch (error) {
    console.error('Feil ved gjenoppretting:', error);
    return false;
  }
}

// 🔥 PAPIRKURV - Slett permanent
async permanentlyDeleteCase(originalCaseId: number): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('deleted_cases')
      .delete()
      .eq('original_case_id', originalCaseId);

    if (error) throw error;

    console.log(`✅ Case ${originalCaseId} permanent slettet`);
    return true;
  } catch (error) {
    console.error('Feil ved permanent sletting:', error);
    return false;
  }
}

// 🧹 PAPIRKURV - Slett gamle cases (eldre enn 30 dager)
async cleanupOldDeletedCases(): Promise<number> {
  try {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const { data, error } = await supabase
      .from('deleted_cases')
      .delete()
      .lt('deleted_at', thirtyDaysAgo.toISOString())
      .select();

    if (error) throw error;

    const deletedCount = data?.length || 0;
    console.log(`🧹 Slettet ${deletedCount} gamle cases fra papirkurv`);
    return deletedCount;
  } catch (error) {
    console.error('Feil ved opprydding:', error);
    return 0;
  }
}

  // 📝 HISTORIKK - Lagre hendelse
  async saveCaseHistory(
  caseId: number, 
  action: string, 
  category: string, 
  userName?: string, 
  details?: any
): Promise<void> {
  try {
    console.log(`📋 Lagrer case history: ${caseId} - ${action}`);
    
    const { error } = await supabase
      .from('case_history')
      .insert({
        case_id: caseId,
        action: action,
        category: category,
        user_name: userName || localStorage.getItem('hagia-user-name') || 'System',
        details: details || {},
        created_at: new Date().toISOString()
      });

    if (error) {
      console.error('Feil ved lagring av case history:', error);
      
      // Sjekk om tabellen mangler kolonner
      if (error.message.includes('column') || error.code === '42703') {
        console.warn('⚠️ case_history tabellen mangler muligens nødvendige kolonner');
        console.warn('Kjør denne SQL-kommandoen i Supabase:');
        console.warn(`
          ALTER TABLE case_history 
          ADD COLUMN IF NOT EXISTS details JSONB DEFAULT '{}',
          ADD COLUMN IF NOT EXISTS user_name VARCHAR(255);
        `);
      }
    } else {
      console.log(`✅ Case history lagret for case ${caseId}`);
    }
  } catch (error) {
    console.error('Error saving case history:', error);
  }
}

  // 📖 HISTORIKK - Hent historikk for en case
  async getCaseHistory(caseId: number): Promise<any[]> {
    try {
      console.log(`📖 Henter historikk for case ${caseId}...`);
      
      const { data, error } = await supabase
        .from('case_history')
        .select('*')
        .eq('case_id', caseId)
        .order('created_at', { ascending: true }); // Endret til ascending for kronologisk rekkefølge

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log(`✅ Hentet ${data?.length || 0} historikk-oppføringer`);
      return data || [];
    } catch (error) {
      console.error('Error loading history:', error);
      return [];
    }
  }

  // ==================== CUSTOMER OPERATIONS ====================

  async loadCustomers(): Promise<Customer[]> {
    try {
      let allCustomers: any[] = [];
      let from = 0;
      const limit = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('customers')
          .select('*')
          .range(from, from + limit - 1)
          .order('name', { ascending: true });

        if (error) throw error;

        if (data && data.length > 0) {
          allCustomers = allCustomers.concat(data);
          from += limit;
          hasMore = data.length === limit;
        } else {
          hasMore = false;
        }
      }

      console.log(`✅ Lastet ${allCustomers.length} kunder fra Supabase`);
      return allCustomers.map(this.dbCustomerToAppCustomer);
    } catch (error) {
      console.error('Error loading customers:', error);
      return [];
    }
  }

  // 💾 CUSTOMER - Lagre/oppdater EN kunde
  async saveCustomer(customer: Customer): Promise<boolean> {
  try {
    // Sørg for at ID er et gyldig heltall
    const customerId = typeof customer.id === 'number' 
      ? customer.id 
      : parseInt(customer.id?.toString() || Date.now().toString().slice(-8));
    
    const dbCustomer = {
      ...this.appCustomerToDbCustomer(customer),
      id: customerId
    };
    
    console.log('Lagrer kunde:', dbCustomer);
    
    // Bruk upsert for å håndtere både nye og eksisterende kunder
    const { data, error } = await supabase
      .from('customers')  // VIKTIG: Bruk customers, IKKE customers_backup
      .upsert(dbCustomer, {
        onConflict: 'id'
      })
      .select();
      
    if (error) {
      console.error('Supabase error ved lagring av kunde:', error);
      
      // Hvis ID-konflikt, prøv med ny ID
      if (error.code === '23505') {
        const newId = parseInt(Date.now().toString().slice(-8));
        dbCustomer.id = newId;
        
        const { error: retryError } = await supabase
          .from('customers')
          .insert(dbCustomer);
          
        if (retryError) {
          console.error('Retry feilet:', retryError);
          return false;
        }
        console.log(`✅ Kunde lagret med ny ID: ${newId}`);
        return true;
      }
      return false;
    }

    console.log(`✅ Kunde lagret/oppdatert: ${customer.name}`);
    
    // Logg til backup også (men ikke som hovedlagring)
    this.backupCustomer(customer, data ? 'UPDATE' : 'CREATE');
    
    return true;
  } catch (error) {
    console.error('Kritisk feil ved lagring av kunde:', error);
    return false;
  }
}

  // 🗑️ CUSTOMER - Slett
  async deleteCustomer(customerId: number): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', customerId);

      if (error) throw error;

      console.log(`✅ Deleted customer ${customerId}`);
      return true;
    } catch (error) {
      console.error('Error deleting customer:', error);
      return false;
    }
  }

  // ==================== PRODUCT OPERATIONS ====================

  // 📦 PRODUCTS - Load (samme som før)
  async loadProducts(): Promise<any[]> {
    try {
      let allProducts: any[] = [];
      let from = 0;
      const limit = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .range(from, from + limit - 1)
          .order('navn', { ascending: true });

        if (error) throw error;

        if (data && data.length > 0) {
          allProducts = allProducts.concat(data);
          from += limit;
          hasMore = data.length === limit;
        } else {
          hasMore = false;
        }
      }

      return allProducts.map(dbProduct => ({
        varenummer: dbProduct.varenummer,
        navn: dbProduct.navn,
        beskrivelse: dbProduct.beskrivelse || '',
        enhet: dbProduct.enhet || 'stk',
        priseksmva: dbProduct.pris_eks_mva || 0,
        prisinklmva: dbProduct.pris_inkl_mva || 0,
        leverandør: dbProduct.leverandor || '',
        underkategori: dbProduct.underkategori || ''
      }));

    } catch (error) {
      console.error('Error loading products:', error);
      return [];
    }
  }

  // 🧪 TEST CONNECTION
  async testConnection(): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('cases')
        .select('count')
        .limit(1)

      return !error
    } catch (error) {
      return false
    }
  }

  // Legg til disse metodene i SupabaseService.ts klassen

// ==================== VEDLIKEHOLDSMODUS ====================

async setMaintenanceMode(enabled: boolean, message: string = '', estimatedMinutes: number = 30): Promise<boolean> {
  try {
    const maintenanceData = {
      enabled: enabled,
      message: message,
      start_time: new Date().toISOString(),
      estimated_end: new Date(Date.now() + estimatedMinutes * 60000).toISOString(),
      set_by: localStorage.getItem('hagia-user-name') || 'Admin'
    };

    const { error } = await supabase
      .from('system_config')
      .update({ value: maintenanceData })
      .eq('key', 'maintenance_mode');
    
    if (error) throw error;

    // Logg vedlikeholdshendelse
    await this.logSystemEvent(enabled ? 'MAINTENANCE_START' : 'MAINTENANCE_END', {
      message: message,
      estimated_minutes: estimatedMinutes,
      set_by: localStorage.getItem('hagia-user-name')
    });

    console.log(`🔧 Vedlikeholdsmodus ${enabled ? 'aktivert' : 'deaktivert'}`);
    return true;
  } catch (error) {
    console.error('Feil ved setting av vedlikeholdsmodus:', error);
    return false;
  }
}

async getMaintenanceStatus(): Promise<any> {
  try {
    const { data, error } = await supabase
      .from('system_config')
      .select('value')
      .eq('key', 'maintenance_mode')
      .single();
    
    if (error || !data) {
      return { enabled: false };
    }
    
    return data.value;
  } catch (error) {
    console.error('Feil ved henting av vedlikeholdsstatus:', error);
    return { enabled: false };
  }
}

async getActiveUsers(): Promise<any[]> {
  try {
    // Sjekk system_events for nylige aktiviteter (siste 15 min)
    const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000).toISOString();
    
    const { data, error } = await supabase
      .from('system_events')
      .select('user_name, created_at, event_type')
      .gt('created_at', fifteenMinutesAgo)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    // Grupper etter bruker og finn siste aktivitet
    const userActivity = {};
    data.forEach(event => {
      if (!userActivity[event.user_name] || 
          new Date(event.created_at) > new Date(userActivity[event.user_name].lastActivity)) {
        userActivity[event.user_name] = {
          lastActivity: event.created_at,
          lastAction: event.event_type
        };
      }
    });
    
    return Object.entries(userActivity).map(([user, activity]) => ({
      user,
      lastActivity: activity.lastActivity,
      lastAction: activity.lastAction,
      minutesAgo: Math.floor((Date.now() - new Date(activity.lastActivity).getTime()) / 60000)
    }));
  } catch (error) {
    console.error('Feil ved henting av aktive brukere:', error);
    return [];
  }
}

async sendMaintenanceWarning(message: string, minutesUntil: number): Promise<boolean> {
  try {
    // 1. Lagre varsel i system_config
    const warningData = {
      active: true,
      message: message,
      minutes_until: minutesUntil,
      warning_time: new Date().toISOString(),
      expires_at: new Date(Date.now() + minutesUntil * 60000).toISOString()
    };

    // Prøv først å oppdatere hvis den finnes
    const { data: existingData, error: fetchError } = await supabase
      .from('system_config')
      .select('*')
      .eq('key', 'maintenance_warning')
      .single();

    let configResult;
    
    if (existingData) {
      // Oppdater eksisterende rad
      configResult = await supabase
        .from('system_config')
        .update({ value: warningData })
        .eq('key', 'maintenance_warning')
        .select();
    } else {
      // Opprett ny rad
      configResult = await supabase
        .from('system_config')
        .insert({
          key: 'maintenance_warning',
          value: warningData
        })
        .select();
    }

    if (configResult.error) {
      console.error('Feil ved lagring av varsel:', configResult.error);
      
      // Prøv alternativ metode med upsert
      const { error: upsertError } = await supabase
        .from('system_config')
        .upsert({
          key: 'maintenance_warning',
          value: warningData
        }, {
          onConflict: 'key'
        })
        .select();
      
      if (upsertError) {
        console.error('Upsert feilet også:', upsertError);
        throw upsertError;
      }
    }

    // 2. Send varsel via system_events
    await this.logSystemEvent('MAINTENANCE_WARNING', {
      message: message,
      minutes_until: minutesUntil,
      warning_time: new Date().toISOString()
    });

    // 3. Send broadcast til alle tilkoblede klienter
    const channel = supabase.channel('maintenance-broadcast');
    
    // Subscribe og send
    channel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await channel.send({
          type: 'broadcast',
          event: 'maintenance-warning',
          payload: {
            message: message,
            minutesUntil: minutesUntil,
            timestamp: new Date().toISOString()
          }
        });
        
        setTimeout(() => {
          supabase.removeChannel(channel);
        }, 1000);
      }
    });

    console.log(`⚠️ Vedlikeholdsvarsel sendt: ${minutesUntil} minutter igjen`);
    
    // Verifiser at varselet ble lagret
    const { data: verifyData } = await supabase
      .from('system_config')
      .select('value')
      .eq('key', 'maintenance_warning')
      .single();
    
    console.log('Varsel lagret i database:', verifyData?.value);
    
    return true;
  } catch (error) {
    console.error('Feil ved sending av vedlikeholdsvarsel:', error);
    return false;
  }
}

      async getActiveMaintenanceWarning(): Promise<any> {
  try {
    const { data, error } = await supabase
      .from('system_config')
      .select('value')
      .eq('key', 'maintenance_warning')
      .single();
    
    if (error || !data) {
      return null;
    }
    
    const warning = data.value;
    
    // Sjekk om varselet fortsatt er gyldig
    if (warning.active && new Date(warning.expires_at) > new Date()) {
      return warning;
    }
    
    // Hvis utløpt, slett det
    if (warning.expires_at && new Date(warning.expires_at) <= new Date()) {
      await supabase
        .from('system_config')
        .update({ value: { active: false } })
        .eq('key', 'maintenance_warning');
    }
    
    return null;
  } catch (error) {
    console.error('Feil ved henting av vedlikeholdsvarsel:', error);
    return null;
  }
}

      // Legg også til denne hjelpefunksjonen for å sjekke aktive varsler:
      async getActiveMaintenanceWarning(): Promise<any> {
        try {
          const { data, error } = await supabase
            .from('system_config')
            .select('value')
            .eq('key', 'maintenance_warning')
            .single();
          
          if (error || !data) {
            return null;
          }
          
          const warning = data.value;
          
          // Sjekk om varselet fortsatt er gyldig
          if (warning.active && new Date(warning.expires_at) > new Date()) {
            return warning;
          }
          
          // Hvis utløpt, slett det
          if (warning.expires_at && new Date(warning.expires_at) <= new Date()) {
            await supabase
              .from('system_config')
              .update({ value: { active: false } })
              .eq('key', 'maintenance_warning');
          }
          
          return null;
        } catch (error) {
          console.error('Feil ved henting av vedlikeholdsvarsel:', error);
          return null;
        }
      }
  // ==================== CONVERSION METHODS ====================

  // 🔄 KONVERTERING - Case App → DB
  private appCaseToDbCase(case_: Case) {
    const toDateOrNull = (dateStr: string | undefined) => {
      if (!dateStr || dateStr.trim() === '') return null;
      return dateStr;
    };

    return {
      id: case_.id,
      kunde: case_.kunde,
      dato: case_.dato || new Date().toISOString().split('T')[0],
      maskin_merke: case_.maskinMerke || '',
      beskrivelse: case_.beskrivelse,
      tlf: case_.tlf || '',
      kategori: case_.kategori,
      varelinjer: case_.varelinjer || [],
      betalt: case_.betalt || false,
      faktura: case_.faktura || false,
      betalingsmetode: case_.betalingsmetode || null,
      reklamasjon: case_.reklamasjon || false,
      opprettet: case_.opprettet,
      kommentar: case_.kommentar || '',
      utfort_dato: toDateOrNull(case_.utfortDato),
      utlevert_av: case_.utlevertAv || '',
      avd: case_.avd || '',
      adresse: case_.adresse || '',
      postnr_sted: case_.postnrSted || '',
      orgnr: case_.orgnr || '',
      ref: case_.ref || '',
      mail: case_.mail || '',
      arsmodel: case_.arsmodel || '',
      serie_nr: case_.serienr || '',
      service_rep_utfort: case_.serviceRepUtført || '',
      delebestilling_dato: toDateOrNull(case_.delebestillingDato),
      merking: case_.merking || ''
    }
  }

  // 🔄 KONVERTERING - Case DB → App
  private dbCaseToAppCase(dbCase: any): Case {
    return {
      id: dbCase.id,
      kunde: dbCase.kunde,
      dato: dbCase.dato || new Date().toISOString().split('T')[0],
      avd: dbCase.avd || '',
      adresse: dbCase.adresse || '',
      postnrSted: dbCase.postnr_sted || '',
      orgnr: dbCase.orgnr || '',
      ref: dbCase.ref || '',
      merking: dbCase.merking || '',
      mail: dbCase.mail || '',
      tlf: dbCase.tlf || '',
      maskinMerke: dbCase.maskin_merke || '',
      serienr: dbCase.serie_nr || '',
      arsmodel: dbCase.arsmodel || '',
      beskrivelse: dbCase.beskrivelse,
      varelinjer: dbCase.varelinjer || [],
      delebestillingDato: dbCase.delebestilling_dato || '',
      kommentar: dbCase.kommentar || '',
      utfortDato: dbCase.utfort_dato || '',
      serviceRepUtført: dbCase.service_rep_utfort || '',
      utlevertAv: dbCase.utlevert_av || '',
      betalt: dbCase.betalt || false,
      faktura: dbCase.faktura || false,
      betalingsmetode: dbCase.betalingsmetode || null,
      reklamasjon: dbCase.reklamasjon || false,
      kategori: dbCase.kategori || 'Service',
      opprettet: dbCase.opprettet
    }
  }

  // 🔄 KONVERTERING - Customer App → DB
  private appCustomerToDbCustomer(customer: Customer) {
    return {
      id: customer.id,
      name: customer.name,
      address: customer.address || '',
      postnr: customer.postnr || '',
      sted: customer.sted || '',
      orgnr: customer.orgnr || '',
      tlf: customer.tlf || '',
      email: customer.email || ''
    }
  }

  // 🔄 KONVERTERING - Customer DB → App
  private dbCustomerToAppCustomer(dbCustomer: any): Customer {
    return {
      id: dbCustomer.id,
      name: dbCustomer.name,
      address: dbCustomer.address || '',
      postnr: dbCustomer.postnr || '',
      sted: dbCustomer.sted || '',
      orgnr: dbCustomer.orgnr || '',
      tlf: dbCustomer.tlf || '',
      email: dbCustomer.email || ''
    }
  }
}

export const supabaseService = new SupabaseService()